import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

/** The four attribute dimensions a guest can filter by. */
export type FilterDimension = "menuType" | "diet" | "spice" | "allergens";

/**
 * How a selection filters the list:
 * - "exclude" — hide dishes matching ANY selected attribute. This is the
 *   allergy-safe reading (pick "nuts" and nut dishes disappear) and the
 *   default, because that is what a guest scanning a menu usually wants.
 * - "include" — keep ONLY dishes matching the selection.
 */
export type FilterMode = "include" | "exclude";

interface DishFiltersState {
  selected: Record<FilterDimension, string[]>;
  mode: FilterMode;
  searchQuery: string;
  toggle: (dimension: FilterDimension, id: string) => void;
  /** Replaces a whole dimension at once, for a select-all or a clear. */
  setDimension: (dimension: FilterDimension, ids: string[]) => void;
  setMode: (mode: FilterMode) => void;
  setSearchQuery: (searchQuery: string) => void;
  clearAll: () => void;
}

const EMPTY: Record<FilterDimension, string[]> = {
  menuType: [],
  diet: [],
  spice: [],
  allergens: [],
};

export const useDishFiltersStore = create<DishFiltersState>((set) => ({
  selected: EMPTY,
  mode: "exclude",
  searchQuery: "",
  toggle: (dimension, id) =>
    set((state) => {
      const current = state.selected[dimension];
      return {
        selected: {
          ...state.selected,
          [dimension]: current.includes(id)
            ? current.filter((value) => value !== id)
            : [...current, id],
        },
      };
    }),
  setDimension: (dimension, ids) =>
    set((state) => ({
      selected: { ...state.selected, [dimension]: ids },
    })),
  setMode: (mode) => set({ mode }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  // Clears selections and the query; the mode is a sticky preference.
  clearAll: () => set({ selected: EMPTY, searchQuery: "" }),
}));

export const useFilterSelection = () =>
  useDishFiltersStore((state) => state.selected);
export const useFilterMode = () => useDishFiltersStore((state) => state.mode);
export const useFilterSearchQuery = () =>
  useDishFiltersStore((state) => state.searchQuery);

export const useFilterActions = () =>
  useDishFiltersStore(
    useShallow((state) => ({
      toggle: state.toggle,
      setDimension: state.setDimension,
      setMode: state.setMode,
      setSearchQuery: state.setSearchQuery,
      clearAll: state.clearAll,
    })),
  );
